import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Stack, Typography } from '@mui/material';

import AppButton from '@/shared/ui/AppButton';

import { getSearchCategoryLabelKey } from '../../model/categories';
import { SearchCategory, SearchHitEntry } from '../../model/types';
import SearchResultRow from '../SearchResultRow';
import { SearchResultGroup } from './hooks';

interface Props {
  groups: SearchResultGroup[];
  query: string;
  activeIndex: number;
  onOpen: (entry: SearchHitEntry) => void;
  onActivate: (index: number) => void;
  onViewAll: (category: SearchCategory) => void;
}

const SearchGroupedResults: FC<Props> = ({
  groups,
  query,
  activeIndex,
  onOpen,
  onActivate,
  onViewAll,
}) => {
  const { t } = useTranslation();

  return (
    <>
      {groups.map((group) => (
        <Stack key={group.category} data-testid={`search-lane-${group.category}`}>
          <Stack
            direction="row"
            alignItems="baseline"
            justifyContent="space-between"
            padding="0.75rem 0.625rem 0.375rem"
          >
            <Typography
              sx={{ fontSize: '1rem', fontWeight: 400, lineHeight: 1.25, color: 'grey.900' }}
            >
              {t(getSearchCategoryLabelKey(group.category))}
            </Typography>
            {group.totalCount > group.entries.length ? (
              <AppButton
                variant="ghost"
                size="small"
                onClick={() => onViewAll(group.category)}
                endIcon={<ChevronRightIcon />}
                data-testid={`search-lane-view-all-${group.category}`}
              >
                {t('search.view_all', {
                  total: group.totalCount,
                  category: t(getSearchCategoryLabelKey(group.category)),
                })}
              </AppButton>
            ) : (
              <Typography
                sx={{ fontSize: '0.75rem', fontWeight: 400, lineHeight: 1.25, color: 'grey.700' }}
              >
                {t('search.group_count', { count: group.totalCount })}
              </Typography>
            )}
          </Stack>

          {group.entries.map((entry, index) => (
            <SearchResultRow
              key={entry.hit.id}
              hit={entry.hit}
              category={entry.category}
              query={query}
              isActive={group.startIndex + index === activeIndex}
              onOpen={() => onOpen(entry)}
              onActivate={() => onActivate(group.startIndex + index)}
            />
          ))}
        </Stack>
      ))}
    </>
  );
};

export default SearchGroupedResults;
